import { KeyboardEvent, useState } from "react";
import { ExternalArrow, Tick } from "./icons";
import { PageCtx } from "./ctx";
import { asset, COUNTRIES, DEFAULT_TRIP_LENGTH, MAX_TRIP_TYPES, MONTHS, Region, REGIONS, TRIP_LENGTHS, TRIP_TYPES } from "./data";
import { ASSISTANT_HOME, fill } from "./links";

const capitalise = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

type Selection =
	| { kind: "none" }
	| { kind: "country"; id: string }
	/** A country or city that is not in the list: whatever was typed in the search box. */
	| { kind: "custom"; name: string };

/**
 * "Pick a country. We'll write the prompt.": filters (region, month, trip types, length) and a grid of countries
 * that compose a prompt, opened in the assistant. Region and the search box narrow the grid; month, types and
 * length only shape the prompt. Server render shows the whole grid.
 */
export default function PromptBuilder({ ctx }: { ctx: PageCtx }) {
	const { tr, promptHref, trackCta, assistant } = ctx;
	const [selection, setSelection] = useState<Selection>({ kind: "none" });
	const [region, setRegion] = useState<Region | null>(null);
	const [month, setMonth] = useState<(typeof MONTHS)[number] | null>(null);
	const [types, setTypes] = useState<Array<(typeof TRIP_TYPES)[number]>>([]);
	const [length, setLength] = useState<(typeof TRIP_LENGTHS)[number] | null>(null);
	const [query, setQuery] = useState("");

	const countryName = (id: string) => tr(`country.${id}.name`);

	const q = query.trim().toLowerCase();
	const qRaw = query.trim();
	const visible = COUNTRIES.filter((c) => !(region && c.region !== region) && !(q && !countryName(c.id).toLowerCase().includes(q)));
	const showCustom = !!qRaw && visible.length === 0;

	const selectedName = selection.kind === "country" ? countryName(selection.id) : selection.kind === "custom" ? selection.name : null;
	let prompt: string | null = null;
	if (selectedName) {
		// The preposition depends on the country in most languages (en Italie, au Japon; in der Schweiz), so each
		// listed country carries its own "in <country>" phrase; a typed-in name gets the generic one.
		const inCountry = selection.kind === "country" ? tr(`country.${selection.id}.in`) : fill(tr("build.prompt.inCustom"), { country: selectedName });
		prompt = fill(tr("build.prompt.base"), { length: tr(`build.lengthPhrase.${length ?? DEFAULT_TRIP_LENGTH}`), inCountry });
		if (month) prompt += fill(tr("build.prompt.month"), { month: tr(`build.monthFull.${month}`) });
		if (types.length)
			prompt += fill(tr("build.prompt.types"), { types: types.map((type) => tr(`build.typePhrase.${type}`)).join(tr("build.prompt.typesJoin")) });
		prompt += tr("build.prompt.end");
	}

	const count = showCustom ? tr("build.countNone") : fill(tr(visible.length === 1 ? "build.countOne" : "build.countOther"), { n: visible.length });

	const toggleCountry = (id: string) => setSelection((s) => (s.kind === "country" && s.id === id ? { kind: "none" } : { kind: "country", id }));
	const pickCustom = () => {
		const name = query.trim().replace(/\s+/g, " ");
		if (name) setSelection({ kind: "custom", name: capitalise(name) });
	};
	const toggleType = (type: (typeof TRIP_TYPES)[number]) =>
		setTypes((current) => {
			if (current.includes(type)) return current.filter((x) => x !== type);
			const next = [...current, type];
			return next.length > MAX_TRIP_TYPES ? next.slice(1) : next;
		});

	const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== "Enter") return;
		e.preventDefault();
		if (visible.length) toggleCountry(visible[0].id);
		else if (showCustom) pickCustom();
	};

	const filterButton = (key: string, label: string, pressed: boolean, onClick: () => void, className = "fc") => (
		<button type="button" className={className} key={key} aria-pressed={pressed} onClick={onClick}>
			{label}
		</button>
	);

	return (
		<section className="builder" id="build" aria-labelledby="atp-build-h">
			<div className="wrap">
				<div className="sec-head center">
					<p className="eyebrow">{tr("build.eyebrow")}</p>
					<h2 className="h2" id="atp-build-h">
						{tr("build.h2Pre")}
						<span className="hl">{tr("build.h2Hl")}</span>
					</h2>
					<p className="lede">{tr("build.lede")}</p>
				</div>
				<div className="bd">
					<aside className="bd-side">
						<div className="bd-search">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
								<circle cx="11" cy="11" r="7" />
								<path d="M20 20l-4-4" />
							</svg>
							<input
								type="search"
								placeholder={tr("build.searchPlaceholder")}
								autoComplete="off"
								aria-label={tr("build.searchLabel")}
								value={query}
								onChange={(e) => {
									setQuery(e.target.value);
									// Typing again drops a custom entry; a picked country from the list stays picked.
									setSelection((s) => (s.kind === "custom" ? { kind: "none" } : s));
								}}
								onKeyDown={onSearchKeyDown}
							/>
						</div>
						<div className="bd-group">
							<p>{tr("build.groupRegion")}</p>
							<div className="fcs">{REGIONS.map((r) => filterButton(r, tr(`build.region.${r}`), region === r, () => setRegion(region === r ? null : r)))}</div>
						</div>
						<div className="bd-group">
							<p>{tr("build.groupWhen")}</p>
							<div className="fcs months">
								{MONTHS.map((m) => filterButton(m, tr(`build.month.${m}`), month === m, () => setMonth(month === m ? null : m), "fc m"))}
							</div>
						</div>
						<div className="bd-group">
							<p>{tr("build.groupType")}</p>
							<div className="fcs">{TRIP_TYPES.map((type) => filterButton(type, tr(`build.type.${type}`), types.includes(type), () => toggleType(type)))}</div>
						</div>
						<div className="bd-group">
							<p>{tr("build.groupLength")}</p>
							<div className="fcs len">
								{TRIP_LENGTHS.map((l) => filterButton(l, tr(`build.length.${l}`), length === l, () => setLength(length === l ? null : l), "fc l"))}
							</div>
						</div>
					</aside>
					<div className="bd-main">
						<div className="bd-head">
							<b>{count}</b>
							<span>{tr("build.sort")}</span>
						</div>
						<div className="bd-grid">
							<button type="button" className="cc cc-any" hidden={!showCustom} aria-pressed={selection.kind === "custom"} onClick={pickCustom}>
								<span className="cc-any-ic">
									<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
										<circle cx="12" cy="12" r="9" />
										<path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
									</svg>
								</span>
								<span className="cc-txt">
									<b>{capitalise(qRaw)}</b>
									<small>{tr("build.anyText")}</small>
								</span>
								<span className="cc-check">
									<Tick width={3.4} />
								</span>
							</button>
							{COUNTRIES.map((c) => {
								const name = countryName(c.id);
								const hidden = !visible.includes(c);
								return (
									<button
										type="button"
										className="cc"
										key={c.id}
										hidden={hidden}
										aria-pressed={selection.kind === "country" && selection.id === c.id}
										onClick={() => toggleCountry(c.id)}
									>
										<img src={asset(`countries/${c.id}.jpg`)} alt="" loading="lazy" />
										<span className="cc-flag">
											<img src={asset(`countries/flag-${c.flag}.png`)} alt="" />
											{name}
										</span>
										<span className={`cc-badge${c.badge === "gem" ? " gem" : ""}`}>{tr(c.badge === "gem" ? "build.badgeGem" : "build.badgeMustSee")}</span>
										<span className="cc-txt">
											<b>{name}</b>
											<small>{tr(`country.${c.id}.tag`)}</small>
										</span>
										<span className="cc-check">
											<Tick width={3.4} />
										</span>
									</button>
								);
							})}
						</div>
						<div className="bd-bar">
							<div className="bd-prompt">
								<span className="bd-label">{tr("build.yourPrompt")}</span>
								<p>{prompt ?? tr("build.pick")}</p>
							</div>
							<a
								className="btn btn-green"
								id="bd-go"
								href={prompt ? promptHref(prompt) : ASSISTANT_HOME[assistant]}
								target="_blank"
								rel="noopener"
								data-where="builder"
								aria-disabled={!prompt}
								onClick={() => trackCta("builder", "open_assistant")}
							>
								{tr("build.go")}
								<ExternalArrow />
							</a>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
